import { RootStore } from "./RootStore";
import { currentUser, login, api, register } from "../vendor";
import { observable, action, computed } from "mobx";
import { User, PermissionName } from "../vendor/types";

export class AuthStore {
  protected rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    this.bootstrapAuth();
  }

  @observable public loadingBootstrap = true;
  @observable public user: User | null = null;
  @observable public token: string | null = null;

  public checkAllowAllPartners = () => {
    return !!this.user?.allowAllPartners;
  };

  public checkPermission = (...values: PermissionName[]) => {
    const permissions = this.user!.groupPermission.permissions.map(
      (e) => e.value
    );

    return permissions.some((e) => values.includes(e) || e === "ADMIN_ACCESS");
  };

  @computed
  public get isAuthenticated() {
    if (this.loadingBootstrap) {
      return "pending";
    } else if (this.user) {
      return "logged";
    } else {
      return "notLogged";
    }
  }

  @action
  private bootstrapAuth = async () => {
    let token = this.token;
    if (!token) token = localStorage.getItem("token");
    if (!token) token = sessionStorage.getItem("token");

    this.token = token;
    if (token) {
      api.setHeader("Authorization", `Bearer ${token}`);
      try {
        this.user = await currentUser();
      } catch (err) {
        this.token = null;
        this.user = null;
        localStorage.clear();
        sessionStorage.clear();
        api.removeHeader("Authorization");
      }
    }

    this.loadingBootstrap = false;
  };

  @action
  public logout = () => {
    localStorage.clear();
    sessionStorage.clear();
    api.removeHeader("Authorization");
    window.location.reload();
  };

  @action
  public register = async (data: {
    email: string;
    username: string;
    password: string;
    fullName: string;
  }) => {
    this.rootStore.generalStore.setShowLoading(true);

    try {
      await register(data);

      this.rootStore.routerStore.push("/login");
      this.rootStore.generalStore.showSnackbar(
        "Conta criada com sucesso!",
        "success"
      );
    } catch (err) {
      this.rootStore.generalStore.showSnackbar(
        err.message || "Não foi possível se cadastrar.",
        "error"
      );
    } finally {
      this.rootStore.generalStore.setShowLoading(false);
    }
  };

  @action
  public login = async (
    username: string,
    password: string,
    remember: boolean
  ) => {
    this.rootStore.generalStore.setShowLoading(true);

    const urlParams = new URLSearchParams(window.location.search);
    const path = urlParams.get("path");

    try {
      const response = await login(username, password);

      if (remember) {
        localStorage.setItem("token", response.access_token);
      } else {
        sessionStorage.setItem("token", response.access_token);
      }

      this.user = response.user;
      this.token = response.access_token;
      api.setHeader("Authorization", `Bearer ${response.access_token}`);

      this.rootStore.routerStore.push(path || "/dashboard");
    } catch (err) {
      this.rootStore.generalStore.showSnackbar(
        err.message || "Não foi possível fazer o login.",
        "error"
      );
    } finally {
      this.rootStore.generalStore.setShowLoading(false);
    }
  };
}
